/**
 * CancelSubscriptionModal.tsx - Subscription Cancellation Modal
 *
 * This modal allows users to cancel a subscription with confirmation
 * and reason selection for tracking purposes.
 *
 * Key Features:
 * - Subscription cancellation confirmation
 * - Reason selection for cancellation tracking
 * - Subscription summary display
 * - Warning about cancellation effects
 *
 * Props:
 * - isVisible: boolean - Controls modal visibility
 * - subscription: Subscription | null - Subscription to cancel
 * - onClose: () => void - Close modal handler
 * - onConfirm: (reason: string) => void - Cancellation confirmation handler
 *
 * Dependencies: Subscription type, styled components
 */

import React, { useState } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { X, AlertTriangle, XCircle } from "lucide-react";
import { firstValueFrom } from "rxjs";
import { SubscriptionModel } from "../models";
import {
  CancelSubscriptionTypes,
  IAPICancelSubscriptionModel,
  RefundSubscriptionTypes,
} from "@mindmemobile/mm-payment-modules";
import { FundraiserService } from "../services";
import { AuthService } from "@mindmemobile/mm-shared-global";
import ToastService from "./ToastService";

const ModalOverlay = styled.div<{ visible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: ${(props) => (props.visible ? "flex" : "none")};
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  backdrop-filter: blur(4px);
`;

const ModalContent = styled.div`
  background: #ffffff;
  border-radius: 24px;
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: #f1f5f9;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 10;

  &:hover {
    background: #e2e8f0;
    color: #1e293b;
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const ModalTitleBar = styled.div`
  background: #fef2f2;
  padding: 32px 32px 14px;
  text-align: center;
  border-radius: 24px 24px 0 0;
`;

const ModalTitle = styled.h2`
  font-size: 28px;
  font-weight: 700;
  color: #dc2626;
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
`;

const ModalBody = styled.div`
  padding: 24px 32px 32px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 20px;
`;

const FormLabel = styled.label`
  font-size: 16px;
  font-weight: 600;
  color: #374151;
`;

const PrimaryButton = styled.button<{ disabled?: boolean }>`
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  transition: all 0.2s ease;
  border: none;
  background: ${(props) => (props.disabled ? "#9ca3af" : "#10b981")};
  color: #ffffff;
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};

  &:hover:not(:disabled) {
    background: #059669;
  }
`;

const SecondaryButton = styled.button`
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  background: #f1f5f9;
  color: #64748b;

  &:hover {
    background: #e2e8f0;
  }
`;

const WarningSection = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  background: #fef3c7;
  border: 1px solid #f59e0b;
  border-radius: 12px;
  margin-bottom: 24px;
`;

const WarningIcon = styled.div`
  color: #f59e0b;
  flex-shrink: 0;
  margin-top: 2px;
`;

const WarningContent = styled.div`
  flex: 1;
`;

const WarningTitle = styled.h4`
  font-size: 16px;
  font-weight: 600;
  color: #92400e;
  margin: 0 0 4px 0;
`;

const WarningText = styled.p`
  font-size: 14px;
  color: #92400e;
  margin: 0;
  line-height: 1.4;
`;

const SubscriptionSummary = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 24px;
`;

const SummaryTitle = styled.h4`
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 12px 0;
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const SummaryItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const SummaryLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const SummaryValue = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
`;

const ReasonSelect = styled.select`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 16px;
  color: #1e293b;
  background: #ffffff;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #10b981;
    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
  }
`;

const ReasonTextArea = styled.textarea`
  width: 100%;
  min-height: 80px;
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 16px;
  color: #1e293b;
  resize: vertical;
  font-family: inherit;
  transition: border-color 0.2s ease;
  background: #ffffff;

  &:focus {
    outline: none;
    border-color: #10b981;
    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-top: 24px;
`;

const DangerButton = styled(PrimaryButton)`
  background: #dc2626;

  &:hover:not(:disabled) {
    background: #b91c1c;
  }
`;

interface CancelSubscriptionModalProps {
  isVisible: boolean;
  subscription: SubscriptionModel | null;
  onClose: () => void;
  onConfirm: () => void;
}

export const CancelSubscriptionModal: React.FC<
  CancelSubscriptionModalProps
> = ({ isVisible, subscription, onClose, onConfirm }) => {
  const [reason, setReason] = useState("");
  const [comment, setComment] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    }).format(date);
  };

  const handleConfirm = async () => {
    setIsProcessing(true);
    try {
      const data: IAPICancelSubscriptionModel = {
        accountId: AuthService.authData?.accountId || "",
        id: subscription?.id || "",
        cancellationType: CancelSubscriptionTypes.Immediately,
        customDate: null,
        refundType: RefundSubscriptionTypes.None,
        customAmount: subscription?.monthlyAmount || 0,
        refundPaymentTransactionId: null,
      };
      const response = await firstValueFrom(
        FundraiserService.cancelSubscription(data)
      );
      if (response.result) {
        onConfirm();
        ToastService.showSuccess("Subscription cancelled successfully.");
      } else {
        ToastService.showError("Failed to cancel subscription.");
      }
    } catch (error) {
      ToastService.showError("Failed to cancel subscription.");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    setReason("");
    setComment("");
    onClose();
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        handleClose();
      }
    };

    if (isVisible) {
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "unset";
    };
  }, [isVisible]);

  // const isFormValid = reason && (reason !== "other" || comment.trim());
  const isFormValid = true;

  if (!subscription || !isVisible) {
    return null;
  }

  return createPortal(
    <ModalOverlay visible={isVisible} onClick={handleOverlayClick}>
      <ModalContent>
        <CloseButton onClick={handleClose}>
          <X size={16} />
        </CloseButton>

        <ModalTitleBar>
          <ModalTitle>
            <XCircle size={26} color="#dc2626" />
            Cancel Subscription
          </ModalTitle>
        </ModalTitleBar>

        <ModalBody>
          <WarningSection>
            <WarningIcon>
              <AlertTriangle size={20} />
            </WarningIcon>
            <WarningContent>
              <WarningTitle>Confirm subscription cancellation?</WarningTitle>
              <WarningText>
                This action cannot be undone. The donor will no longer be
                charged monthly, and they will need to create a new subscription
                to resume donations.
              </WarningText>
            </WarningContent>
          </WarningSection>

          <SubscriptionSummary>
            <SummaryGrid>
              <SummaryItem>
                <SummaryLabel>Donor</SummaryLabel>
                <SummaryValue>{subscription.getDisplayName()}</SummaryValue>
              </SummaryItem>
              <SummaryItem>
                <SummaryLabel>Monthly Amount</SummaryLabel>
                <SummaryValue>
                  {formatCurrency(subscription.monthlyAmount)}
                </SummaryValue>
              </SummaryItem>
              <SummaryItem>
                <SummaryLabel>Next Due Date</SummaryLabel>
                <SummaryValue>
                  {formatDate(subscription.nextDueDate)}
                </SummaryValue>
              </SummaryItem>
              <SummaryItem>
                <SummaryLabel>Total Paid</SummaryLabel>
                <SummaryValue>
                  {formatCurrency(subscription.totalPaid)} (
                  {subscription.paymentCount} payments)
                </SummaryValue>
              </SummaryItem>
            </SummaryGrid>
          </SubscriptionSummary>

          {/* <FormGroup>
            <FormLabel>Reason for Cancellation</FormLabel>
            <ReasonSelect
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
            >
              <option value="">Select a reason...</option>
              <option value="donor-request">Donor requested cancellation</option>
              <option value="payment-failed">Payment method failed</option>
              <option value="fundraiser-ended">Fundraiser ended</option>
              <option value="duplicate">Duplicate subscription</option>
              <option value="other">Other</option>
            </ReasonSelect>
          </FormGroup> */}

          {reason === "other" && (
            <FormGroup>
              <FormLabel>Please specify</FormLabel>
              <ReasonTextArea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Enter the reason for cancellation..."
                required
              />
            </FormGroup>
          )}

          <ButtonGroup>
            <SecondaryButton onClick={handleClose}>
              Do Not Cancel
            </SecondaryButton>
            <DangerButton onClick={handleConfirm} disabled={isProcessing}>
              Process Cancellation
            </DangerButton>
          </ButtonGroup>
        </ModalBody>
      </ModalContent>
    </ModalOverlay>,
    document.body
  );
};
